import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import NotFound from "../components/NotFound";
import { PrimaryLink, Reveal, usePageMeta } from "../components/Common";
import { ArticleBody } from "../components/ArticleBody";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Article = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | missing

  useEffect(() => {
    let live = true;
    setState("loading");
    axios
      .get(`${API}/articles/${slug}`)
      .then((r) => {
        if (!live) return;
        setArticle(r.data);
        setState("ok");
      })
      .catch(() => live && setState("missing"));
    return () => {
      live = false;
    };
  }, [slug]);

  usePageMeta(
    article
      ? article.seo_title || `${article.title} — English for Leaders`
      : "English for Leaders",
    article ? article.seo_description || article.excerpt : undefined,
    `/insights/${slug}`
  );

  if (state === "missing") return <NotFound />;
  if (state === "loading" || !article) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-28" data-testid="article-loading">
        <p className="text-sm font-semibold text-charcoal/50">Loading article…</p>
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      <section className="border-b border-navy/10 bg-offwhite" data-testid="article-hero">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-crimson">
              {article.category}
            </p>
            <h1 className="mt-5 font-display text-3xl leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {article.title}
            </h1>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">
              David Duenzl · Ideas, Questions &amp; Possibilities · {article.read_time}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-14 lg:py-20">
          <Reveal>
            <ArticleBody blocks={article.body} />

            <div className="mt-14 bg-navy p-8 sm:p-10" data-testid="article-cta">
              <h2 className="font-display text-2xl leading-snug text-white">
                Discuss how this applies to your school or organisation.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                Every engagement begins with a straightforward conversation.
              </p>
              <div className="mt-6">
                <PrimaryLink to="/contact" testid="article-cta-button">
                  Start a Conversation
                </PrimaryLink>
              </div>
            </div>

            <Link
              to="/insights"
              data-testid="article-back-link"
              className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-charcoal/60 transition-colors hover:text-navy"
            >
              <ArrowLeft className="h-4 w-4" />
              Ideas, Questions &amp; Possibilities
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Article;
